// admin/frequencia-grid.js — consulta de frequência por turma (somente leitura).
//
// Origem (monólito): linhas 7080-7240 (renderAdminFrequencia), incluindo
//   tabs por turma, tabela de alunos com status de frequência.
//
// v19.0.3 (R13 — redesign mobile):
//   - Tabs de turma substituídas por picker iOS-style (wheel giratória).
//   - Linha do aluno: só avatar + nome completo (até 2 linhas) + badge %.
//   - Colunas de presenças/faltas removidas daqui (ficam no modal detalhe).
//   - Badge apresenta apenas percentual (sem ícones) — legenda de cores abaixo.
//
// Dependências:
//   - api     (core/api.js)     — api.get('alunos'), api.get('frequencia')
//   - auth    (core/auth.js)    — auth.getCurrent() (guarda admin)
//   - notify  (core/notify.js)  — notify.error()
//   - router  (core/router.js)  — router.go() para voltar ao dashboard
//
// Exporta:
//   function render(container) — monta a grade de frequência.

import { api }    from '../core/api.js';
import { notify } from '../core/notify.js';
import { router } from '../core/router.js';
import { storage } from '../core/storage.js';
import { skelTable } from '../core/skeletons.js';
import * as freqDetail from './frequencia-detail.js';
import { openTurmaPicker } from './turma-picker.js';

// Filtro de turmas ativas: somente 31+ (turmas anteriores já encerraram).
// Mantém paridade com v18 ll. 3578.
const TURMA_MINIMA = 31;

function nomeKey(nome) {
  return String(nome || '').trim().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

const NOME_CONECTORES = ['de','da','do','dos','das','e','von','van','di','del','della','la','le','y'];
function formatNome(nome) {
  if (!nome) return '';
  return String(nome).toLowerCase().split(' ').map((palavra, i) => {
    if (!palavra) return palavra;
    if (i > 0 && NOME_CONECTORES.includes(palavra)) return palavra;
    return palavra.charAt(0).toUpperCase() + palavra.slice(1);
  }).join(' ');
}

function lookupFreq(freqMap, aluno) {
  if (!freqMap || !aluno) return null;
  return freqMap[nomeKey(aluno._nomeRaw || aluno.nome)]
      || freqMap[aluno.cpf]
      || freqMap[aluno.senha]
      || null;
}

function countPresFaltas(fd) {
  if (!fd) return { totalP: 0, totalF: 0 };
  if (Array.isArray(fd.registros)) {
    let p = 0, f = 0;
    for (const r of fd.registros) {
      if (r.status === 'presente') p++;
      else if (r.status === 'falta') f++;
    }
    return { totalP: p, totalF: f };
  }
  const totalP = Object.values(fd.presencas || {}).reduce((a, b) => a + (b || 0), 0);
  const totalF = Object.values(fd.faltas    || {}).reduce((a, b) => a + (b || 0), 0);
  return { totalP, totalF };
}

// ── Status ────────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  sem_dados: { cor: '#9B9B9B', bg: '#F0F0F0' },
  ok:        { cor: '#1B8A3A', bg: '#E6F7EB' },
  atencao:   { cor: '#B45309', bg: '#FFF3E0' },
  critico:   { cor: '#C62828', bg: '#FCE4EC' },
};

function calcStatus(fd) {
  if (!fd) return { totalP: 0, totalF: 0, pct: 0, cfg: STATUS_CONFIG.sem_dados, label: '—' };
  const { totalP, totalF } = countPresFaltas(fd);
  const total = totalP + totalF;
  const pct   = total > 0 ? Math.round((totalP / total) * 100) : 0;

  let cfg, label;
  if (total === 0) {
    cfg = STATUS_CONFIG.sem_dados; label = '—';
  } else if (totalF === 0) {
    cfg = STATUS_CONFIG.ok;      label = '100%';
  } else if (pct >= 75) {
    cfg = STATUS_CONFIG.atencao; label = `${pct}%`;
  } else {
    cfg = STATUS_CONFIG.critico; label = `${pct}%`;
  }
  return { totalP, totalF, pct, cfg, label };
}

// Iniciais 2 letras (a partir do nome formatado, pulando conectores)
function iniciais(nome) {
  const parts = String(nome || '').split(/\s+/)
    .filter(p => p && !NOME_CONECTORES.includes(p.toLowerCase()));
  const a = parts[0]?.[0] || '';
  const b = parts[parts.length - 1]?.[0] || '';
  return (a + (parts.length > 1 ? b : '')).toUpperCase();
}

// Escape mínimo pra nome em atributos/texto
function esc(s) {
  return String(s || '').replace(/[&<>"']/g, m => ({
    '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
  }[m]));
}

// ── Tabela da turma ───────────────────────────────────────────────────────────

function _buildTabela(alunos, freqMap, turmaNum) {
  const sorted = [...alunos].sort((a, b) => {
    const fA = lookupFreq(freqMap, a);
    const fB = lookupFreq(freqMap, b);
    const fltA = fA ? countPresFaltas(fA).totalF : -1;
    const fltB = fB ? countPresFaltas(fB).totalF : -1;
    return fltB - fltA;
  });

  let rows = '';
  sorted.forEach((aluno, idx) => {
    const fd = lookupFreq(freqMap, aluno);
    const { cfg, label } = calcStatus(fd);
    const fundo = idx % 2 === 0 ? '#fff' : '#FAFAFA';
    const ini = iniciais(aluno.nome);

    rows += `
      <div class="freq-row" data-idx="${idx}" style="
        display:flex;align-items:center;gap:12px;
        padding:12px 14px;border-bottom:1px solid #F0F0F0;
        background:${fundo};cursor:pointer;transition:background .15s;
      ">
        <div style="
          width:38px;height:38px;border-radius:50%;
          background:linear-gradient(135deg,#6A001F,#B3002D);
          display:flex;align-items:center;justify-content:center;
          font-size:12px;font-weight:800;color:#fff;flex-shrink:0;letter-spacing:.3px;
        ">${ini}</div>
        <div style="
          flex:1;min-width:0;font-size:14px;font-weight:700;color:#0A0A0A;
          line-height:1.25;
          display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;
          word-break:break-word;
        ">${esc(aluno.nome)}</div>
        <span style="
          flex-shrink:0;font-size:12px;font-weight:800;white-space:nowrap;
          padding:5px 11px;border-radius:100px;
          color:${cfg.cor};background:${cfg.bg};
        ">${label}</span>
      </div>`;
  });

  return `
    <div style="background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.07);margin-bottom:12px">
      <div style="
        background:linear-gradient(90deg,#6A001F,#B3002D);
        padding:12px 16px;display:flex;align-items:center;justify-content:space-between;
      ">
        <div style="font-size:11px;font-weight:700;color:rgba(255,255,255,0.85);text-transform:uppercase;letter-spacing:1.2px">
          Aluno · Turma ${turmaNum}
        </div>
        <div style="font-size:11px;font-weight:700;color:rgba(255,255,255,0.85);text-transform:uppercase;letter-spacing:1.2px">
          Status
        </div>
      </div>
      ${rows}
      <div style="padding:10px 16px;font-size:11px;font-weight:600;color:#9B9B9B;border-top:1px solid #F0F0F0;text-align:right">
        ${sorted.length} aluno${sorted.length !== 1 ? 's' : ''}
      </div>
    </div>
    <div style="display:flex;gap:14px;flex-wrap:wrap;padding:4px 2px 8px">
      <span style="font-size:11px;font-weight:600;color:#1B8A3A">● 100% sem faltas</span>
      <span style="font-size:11px;font-weight:600;color:#B45309">● Atenção ≥75%</span>
      <span style="font-size:11px;font-weight:600;color:#C62828">● Crítico &lt;75%</span>
      <span style="font-size:11px;font-weight:600;color:#9B9B9B">● Sem dados</span>
    </div>`;
}

// ── Render principal ──────────────────────────────────────────────────────────

export async function render(container) {
  container.innerHTML = `
    <div style="min-height:100vh;min-height:100dvh;background:#F5F5F5;font-family:'Montserrat',sans-serif;">
      <header style="background:linear-gradient(90deg,#6A001F,#B3002D);padding:calc(env(safe-area-inset-top,0px) + 16px) 20px 16px;position:sticky;top:0;z-index:100;">
        <div style="display:flex;align-items:center;gap:14px">
          <button id="freqBackBtn" style="width:38px;height:38px;border-radius:50%;border:none;background:rgba(255,255,255,0.18);color:#fff;font-size:18px;cursor:pointer;flex-shrink:0;">←</button>
          <div>
            <div style="font-size:10px;font-weight:600;color:rgba(255,255,255,0.65);letter-spacing:2px;text-transform:uppercase">Admin</div>
            <div style="font-size:20px;font-weight:800;color:#fff">Frequência dos Alunos</div>
          </div>
        </div>
      </header>
      <main id="freqGridMain" style="padding:16px;padding-bottom:calc(env(safe-area-inset-bottom,0px) + 80px)" aria-busy="true">
        ${skelTable({ rows: 6, cols: 3 })}
      </main>
    </div>`;

  container.querySelector('#freqBackBtn').addEventListener('click', () => router.go('/admin'));

  const main = container.querySelector('#freqGridMain');

  let alunosList  = [];
  let freqData    = {};
  let programacao = null;

  try {
    const cached = storage.get('alunos_cache', null);
    const alunosPromise = cached && Array.isArray(cached.alunos)
      ? Promise.resolve(cached.alunos)
      : api.get('alunos', { useMemCache: true });
    const freqPromise = api.get('frequencia', { useMemCache: true })
      .catch(e => {
        console.warn('[frequencia-grid] frequência indisponível:', e);
        return {};
      });

    const [alunosRes, freqRes] = await Promise.all([alunosPromise, freqPromise]);
    alunosList = alunosRes;
    freqData   = freqRes || {};
  } catch (e) {
    notify.error('Não foi possível carregar a lista de alunos.');
    console.error('[frequencia-grid] erro ao buscar dados:', e);
  }

  const programacaoPromise = api.getProgramacao()
    .then(p => { programacao = p || []; })
    .catch(e => {
      console.warn('[frequencia-grid] programação indisponível:', e);
      programacao = [];
    });

  if (!main.isConnected) return;

  // Dedup (v19.0.2 / R12.2)
  const seen = new Set();
  const alunos = (Array.isArray(alunosList) ? alunosList : [])
    .filter(a => {
      if (a.turma === 'Admin') return false;
      const n = parseInt(a.turma, 10);
      if (!Number.isFinite(n) || n < TURMA_MINIMA) return false;
      const cpfKey = String(a.cpf || a.senha || '').replace(/\D/g, '');
      const key = (cpfKey || nomeKey(a.nome)) + '|' + a.turma;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(a => ({
      ...a,
      senha:    a.senha || a.cpf,
      cpf:      a.cpf   || a.senha,
      _nomeRaw: a.nome,
      nome:     formatNome(a.nome),
    }));

  if (!alunos.length) {
    main.innerHTML = `
      <div style="background:#fff;border-radius:16px;padding:32px;text-align:center;box-shadow:0 2px 12px rgba(0,0,0,0.07)">
        <div style="font-size:32px;margin-bottom:12px">📋</div>
        <div style="font-size:15px;font-weight:700;color:#0A0A0A;margin-bottom:8px">Nenhum aluno cadastrado</div>
        <div style="font-size:13px;color:#9B9B9B">Importe uma planilha em <strong>Importar Planilhas</strong>.</div>
        <button id="irImportarBtn" style="margin-top:16px;padding:12px 24px;background:linear-gradient(90deg,#6A001F,#B3002D);color:#fff;border:none;border-radius:12px;font-family:'Montserrat',sans-serif;font-weight:700;cursor:pointer;font-size:13px">
          Ir para Importar
        </button>
      </div>`;
    container.querySelector('#irImportarBtn').addEventListener('click', () => router.go('/admin/importar'));
    return;
  }

  // Agrupa por turma
  const turmas = {};
  alunos.forEach(a => {
    if (!turmas[a.turma]) turmas[a.turma] = [];
    turmas[a.turma].push(a);
  });
  const turmaKeys = Object.keys(turmas).sort((a, b) => Number(a) - Number(b));
  const counts = Object.fromEntries(turmaKeys.map(t => [t, turmas[t].length]));

  let activeTurma = turmaKeys[0];

  main.innerHTML = `
    <button id="turmaSelectBtn" style="
      width:100%;display:flex;align-items:center;justify-content:space-between;
      padding:14px 18px;margin-bottom:14px;
      background:#fff;border:1.5px solid #E8E8E8;border-radius:14px;
      box-shadow:0 2px 8px rgba(0,0,0,0.05);cursor:pointer;font-family:inherit;
    ">
      <div style="display:flex;flex-direction:column;align-items:flex-start;gap:2px">
        <span style="font-size:10px;font-weight:700;color:#9B9B9B;letter-spacing:1.5px;text-transform:uppercase">Turma</span>
        <span id="turmaSelectLabel" style="font-size:17px;font-weight:800;color:#0A0A0A"></span>
      </div>
      <div style="display:flex;align-items:center;gap:10px">
        <span id="turmaSelectCount" style="font-size:12px;font-weight:700;color:#6A001F;background:#FDECEE;padding:4px 10px;border-radius:100px"></span>
        <span style="color:#9B9B9B;font-size:18px;line-height:1">⌄</span>
      </div>
    </button>
    <div id="tabelaContainer"></div>`;

  const btnSel   = main.querySelector('#turmaSelectBtn');
  const lblSel   = main.querySelector('#turmaSelectLabel');
  const lblCount = main.querySelector('#turmaSelectCount');
  const tabelaContainer = main.querySelector('#tabelaContainer');

  function renderTurma(turmaNum) {
    activeTurma = turmaNum;
    lblSel.textContent   = `Turma ${turmaNum}`;
    lblCount.textContent = `${counts[turmaNum] || 0} aluno${(counts[turmaNum] || 0) !== 1 ? 's' : ''}`;
    tabelaContainer.innerHTML = _buildTabela(turmas[turmaNum], freqData, turmaNum);
  }

  btnSel.addEventListener('click', () => {
    openTurmaPicker(turmaKeys, counts, activeTurma, (t) => renderTurma(t));
  });

  // Abre detalhe do aluno ao clicar na linha (R1)
  tabelaContainer.style.cursor = 'pointer';
  tabelaContainer.addEventListener('click', async e => {
    const row = e.target.closest('.freq-row');
    if (!row) return;
    const idx = Number(row.dataset.idx);
    const turmaAlunos = turmas[activeTurma];
    const sorted = [...turmaAlunos].sort((a, b) => {
      const fA = lookupFreq(freqData, a);
      const fB = lookupFreq(freqData, b);
      const fltA = fA ? countPresFaltas(fA).totalF : -1;
      const fltB = fB ? countPresFaltas(fB).totalF : -1;
      return fltB - fltA;
    });
    const aluno = sorted[idx];
    if (!aluno) return;
    if (programacao === null) {
      try { await programacaoPromise; } catch { /* ignorado */ }
    }
    freqDetail.open(aluno, freqData, programacao || []);
  });

  tabelaContainer.addEventListener('keydown', e => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const row = e.target.closest('.freq-row');
    if (!row) return;
    e.preventDefault();
    row.click();
  });

  renderTurma(activeTurma);
}
